import {
  POLARITY_DEFAULT_COLORS,
  POLARITY_LABELS,
  type EvaluationPolarity,
} from '@citizen-walk/shared-types';
import {
  barFill,
  barFillBackgroundSize,
  POLARITY_DISPLAY_ORDER,
  POLARITY_HATCH_DESCRIPTIONS,
  STACK_SEPARATOR_STYLE,
} from './barFillStyles';
import { PolarityLegend } from './PolarityLegend';
import { useChartTooltip } from './useChartTooltip';

/**
 * エリア選択集計・路線集計の横棒グラフ (2026-07-16、`AreaRadarChart.tsx` の置き換え)。
 *
 * レーダーチャートは、検出要素数が特定の役割に大きく偏るとスパイク状になり読み取れなくなるため
 * 採用しない (docs/25 §10-4)。
 *
 * docs/27 §9 (2026-09-07): `polarity` (評価の内訳) があるときは棒の中を評価で分ける。
 * 2026-09-16 (オーナー指示): 色は調査項目の色を薄くしたものに揃え、評価の区分は**濃淡と模様**で
 * 区別する。積み上げの境目は白いスリットで区切り、ポインタを乗せた部分の件数はその場に出す
 * (スマホでは触れたときに出る)。`stackByPolarity=false` なら評価で分けずに合計だけを 1 本で描く。
 */

export interface AreaBarChartAxis {
  key: string;
  label: string;
  value: number;
  color: string;
  /** 評価の内訳 (評価軸を持つテンプレートのみ)。 */
  polarity?: { positive: number; negative: number; neutral: number };
  /** この棒自体が評価の区分を表す場合の極性 (模様の決定に使う)。 */
  polarityKind?: EvaluationPolarity;
}

export interface AreaBarChartProps {
  axes: AreaBarChartAxis[];
  /** 評価の内訳で積み上げるか (既定 true)。false なら合計だけの 1 本。 */
  stackByPolarity?: boolean;
  /** 評価の区分の表示名 (評価の定義の `polarityLabels`)。 */
  polarityLabels?: Record<EvaluationPolarity, string>;
}

const POLARITY_ORDER = POLARITY_DISPLAY_ORDER;

export function AreaBarChart({
  axes,
  stackByPolarity = true,
  polarityLabels = POLARITY_LABELS,
}: AreaBarChartProps) {
  const tooltip = useChartTooltip();
  if (axes.length === 0) {
    return <p className="text-sm text-slate-400">分類軸がありません。</p>;
  }
  const sorted = [...axes].sort((a, b) => b.value - a.value);
  const maxValue = Math.max(1, ...sorted.map((a) => a.value));
  const hasPolarity = stackByPolarity && sorted.some((a) => a.polarity);

  return (
    <div className="grid grid-cols-1 gap-2">
      {/* 評価の見分け方はグラフの上に置く (下だとスクロールしないと見えない)。 */}
      {hasPolarity && (
        <PolarityLegend colorNote="色は調査項目を表します。" polarityLabels={polarityLabels} />
      )}
      {sorted.map((a) => {
        const widthPct = (a.value / maxValue) * 100;
        const p = stackByPolarity ? a.polarity : undefined;
        const evaluated = p ? p.positive + p.negative + p.neutral : 0;
        const rest = Math.max(0, a.value - evaluated);
        const rowLines = p
          ? [
              ...POLARITY_ORDER.map((k) => ({
                label: polarityLabels[k],
                value: `${p[k]} 件`,
                color: barFill(a.color, k).background,
              })),
              ...(rest > 0
                ? [{ label: '評価なし', value: `${rest} 件`, color: barFill(a.color).background }]
                : []),
              { label: '合計', value: `${a.value} 件` },
            ]
          : [{ label: '件数', value: `${a.value} 件` }];
        return (
          <div
            key={a.key}
            className="flex items-center gap-2 text-xs sm:text-sm"
            onMouseMove={(e) => tooltip.show(e, a.label, rowLines)}
            onMouseLeave={tooltip.hide}
            onTouchStart={(e) => tooltip.show(e, a.label, rowLines)}
            onTouchEnd={tooltip.hide}
          >
            {/* 画面が狭いときはラベル列を詰める (スマホ対応)。 */}
            <span className="flex w-24 shrink-0 items-center gap-1.5 sm:w-40">
              <span
                className="h-3 w-3 shrink-0 rounded-full border border-slate-300"
                style={{
                  ...barFill(a.color, a.polarityKind ?? null),
                  backgroundSize: barFillBackgroundSize(a.polarityKind ?? null),
                }}
              />
              <span className="truncate text-slate-700">{a.label}</span>
            </span>
            <span className="h-4 min-w-0 flex-1 overflow-hidden rounded bg-slate-100">
              {p && a.value > 0 ? (
                <span
                  className="flex h-full overflow-hidden rounded"
                  style={{ width: `${widthPct}%` }}
                >
                  {POLARITY_ORDER.map((k) =>
                    p[k] > 0 ? (
                      <span
                        key={k}
                        className="block h-full cursor-default transition-opacity hover:opacity-75"
                        style={{
                          width: `${(p[k] / a.value) * 100}%`,
                          ...barFill(a.color, k),
                          backgroundSize: barFillBackgroundSize(k),
                          ...STACK_SEPARATOR_STYLE,
                        }}
                        onMouseMove={(e) => {
                          e.stopPropagation();
                          tooltip.show(e, `${a.label}／${polarityLabels[k]}`, [
                            { label: '件数', value: `${p[k]} 件` },
                            { label: 'この項目の合計', value: `${a.value} 件` },
                            { label: '模様', value: POLARITY_HATCH_DESCRIPTIONS[k] },
                          ]);
                        }}
                      />
                    ) : null,
                  )}
                  {rest > 0 && (
                    <span
                      className="block h-full"
                      style={{ width: `${(rest / a.value) * 100}%`, ...barFill(a.color) }}
                    />
                  )}
                </span>
              ) : (
                <span
                  className="block h-full rounded"
                  style={{
                    width: `${widthPct}%`,
                    ...barFill(a.color, a.polarityKind ?? null),
                    backgroundSize: barFillBackgroundSize(a.polarityKind ?? null),
                  }}
                />
              )}
            </span>
            <span className="w-8 shrink-0 text-right font-semibold tabular-nums text-slate-700">
              {a.value}
            </span>
            {hasPolarity && (
              <span className="hidden w-20 shrink-0 text-right text-[11px] tabular-nums sm:inline">
                {p ? (
                  <>
                    {/* 表示順はポジティブ → 中立 → ネガティブ (積み上げと揃える)。 */}
                    <span style={{ color: POLARITY_DEFAULT_COLORS.positive }}>+{p.positive}</span>
                    {p.neutral > 0 && (
                      <>
                        {' '}
                        <span style={{ color: POLARITY_DEFAULT_COLORS.neutral }}>○{p.neutral}</span>
                      </>
                    )}{' '}
                    <span style={{ color: POLARITY_DEFAULT_COLORS.negative }}>−{p.negative}</span>
                  </>
                ) : (
                  '—'
                )}
              </span>
            )}
          </div>
        );
      })}
      {tooltip.node}
    </div>
  );
}
